import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Logo from './Logo';
import {
  BUYER_APP_QR_URL,
  buyerAppStoreAnchorProps,
  buyerPlayStoreAnchorProps,
} from '../constants/appLinks';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 text-gray-300 mt-auto">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <Logo size="sm" whiteDrop={true} />
            </Link>
            <p className="text-sm text-gray-400 mb-4 max-w-md">
              Your local marketplace for buying and selling items. Connect with your community and discover great deals nearby.
            </p>
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Download BuyDrop app</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <a
                  {...buyerAppStoreAnchorProps()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-semibold ring-1 ring-gray-700 hover:ring-green-500/60 hover:bg-gray-700 transition"
                  aria-label="Download BuyDrop on the App Store"
                >
                  <i className="ri-apple-fill text-lg" aria-hidden />
                  <span>App Store</span>
                </a>
                <a
                  {...buyerPlayStoreAnchorProps()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-semibold ring-1 ring-gray-700 hover:ring-green-500/60 hover:bg-gray-700 transition"
                  aria-label="Get BuyDrop on Google Play"
                >
                  <i className="ri-google-play-fill text-lg text-green-400" aria-hidden />
                  <span>Google Play</span>
                </a>
              </div>
              <div
                className="inline-block rounded-xl bg-white p-2 shadow-md ring-1 ring-gray-700/50"
                aria-label="Scan QR code to download BuyDrop on iOS or Android"
              >
                <QRCodeSVG
                  value={BUYER_APP_QR_URL}
                  size={120}
                  level="M"
                  marginSize={2}
                  title="Scan to download BuyDrop — iOS and Android"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 max-w-xs">
                Scan with your phone. Opens the App Store or Google Play.
              </p>
            </div>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-white transition" aria-label="Facebook">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition" aria-label="Twitter">
                <i className="ri-twitter-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition" aria-label="Instagram">
                <i className="ri-instagram-fill text-xl"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:text-white transition">
                  Browse Listings
                </Link>
              </li>
              <li>
                <Link to="/sell" className="text-sm hover:text-white transition">
                  Seller app
                </Link>
              </li>
              <li>
                <Link to="/search" className="text-sm hover:text-white transition">
                  Search
                </Link>
              </li>
              <li>
                <Link to="/help" className="text-sm hover:text-white transition">
                  Help & Support
                </Link>
              </li>
              <li>
                <Link to="/money-back-guarantee" className="text-sm hover:text-white transition">
                  Money Back Guarantee
                </Link>
              </li>
              <li>
                <a
                  {...buyerAppStoreAnchorProps()}
                  className="text-sm hover:text-white transition"
                >
                  BuyDrop on App Store
                </a>
              </li>
              <li>
                <a
                  {...buyerPlayStoreAnchorProps()}
                  className="text-sm hover:text-white transition"
                >
                  BuyDrop on Google Play
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/terms" className="text-sm hover:text-white transition">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm hover:text-white transition">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-sm hover:text-white transition">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <a href="mailto:support@buydrop.com" className="text-sm hover:text-white transition">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Buy Drop. All rights reserved.
            </p>

            <div className="flex items-center justify-center md:justify-end space-x-2 text-sm text-gray-400">
              <i className="ri-shield-check-line" aria-hidden />
              <span>Secure & Trusted</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
